/**
 * Supabase Auth for the portal.
 *
 * Staff sign in with Google Workspace SSO. The email-domain allowlist is
 * enforced in the database as well as in the provider configuration — a
 * misconfigured provider would otherwise let anyone self-register, and the
 * database trigger is what makes that impossible rather than merely unlikely.
 *
 * The anon key here and the widget's project keys are unrelated credentials.
 * They never authenticate against each other's endpoints, and nothing in this
 * file is reachable from widget code.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Injected at build time.
 *
 * The publishable key is public by design — it authorises nothing on its own,
 * because RLS decides everything — but it still belongs in the build config
 * rather than in source.
 *
 * `sb_publishable_…`, not the legacy `anon` JWT. They are interchangeable here,
 * and the new one is the one Supabase issues now.
 */
const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'] as string | undefined;

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  if (client !== null) return client;
  if (SUPABASE_URL === undefined || SUPABASE_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      'The portal is missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY. Check the build configuration.',
    );
  }
  client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
  return client;
}

export function isConfigured(): boolean {
  return SUPABASE_URL !== undefined && SUPABASE_PUBLISHABLE_KEY !== undefined;
}

/** Where the browser is sent to authenticate. `/api/*` sits behind Access. */
export function accessSignInUrl(returnTo = '/'): string {
  return `/api/auth/session?return=${encodeURIComponent(returnTo)}`;
}

/**
 * Is Access in front of us?
 *
 * Three answers, not two. `unknown` is the one that matters: when Access
 * protects `/api` but not `/`, this fetch is redirected to the Cloudflare login
 * on another origin, and the browser reports that as a network failure
 * indistinguishable from the endpoint being down. Reading that as "not
 * configured" would put an error on the page of a deployment that is set up
 * perfectly well — so an unknown answer shows the button, and the button tells
 * the truth either way: it either signs you in or it lands on a 401 that says
 * why.
 */
export type AccessState = 'configured' | 'unconfigured' | 'unknown';

export type StaffSignIn = {
  readonly access: AccessState;
  /**
   * Local development only, where Access is not in front of anything: staff
   * sign in with the password on their local account instead. The Worker only
   * says yes when `ENVIRONMENT` is `development` and Access is not configured.
   */
  readonly devPassword: boolean;
};

export async function staffSignInOptions(): Promise<StaffSignIn> {
  try {
    const response = await fetch('/api/auth/status');
    // A redirect we could not follow, or anything else non-JSON. The edge is
    // answering, which is itself evidence Access is there.
    if (!response.ok) return { access: 'unknown', devPassword: false };
    const body = (await response.json()) as { configured?: boolean; devPasswordSignIn?: boolean };
    return {
      access: body.configured === true ? 'configured' : 'unconfigured',
      devPassword: body.devPasswordSignIn === true,
    };
  } catch {
    return { access: 'unknown', devPassword: false };
  }
}

/** The local-development staff sign-in. Never offered where Access is configured. */
export async function staffPasswordSignIn(email: string, password: string): Promise<void> {
  const { error } = await supabase().auth.signInWithPassword({ email, password });
  if (error !== null) throw new Error('That email address and password did not match.');
}

/**
 * Finish the round trip.
 *
 * The Worker redirects back with a one-time token in the fragment, which the
 * Supabase client exchanges for a real session — one that lives in its own
 * storage and refreshes itself.
 *
 * The fragment is cleared before the exchange rather than after: a token left
 * in the address bar is one that gets pasted into a chat window eventually,
 * and clearing it first means a failed exchange cannot be retried by reloading
 * with a token that has already been spent.
 */
export async function completeAccessSignIn(): Promise<boolean> {
  const fragment = window.location.hash.replace(/^#/, '');
  if (fragment.length === 0) return false;

  const token = new URLSearchParams(fragment).get('relay_token');
  if (token === null || token.length === 0) return false;

  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

  const { error } = await supabase().auth.verifyOtp({ token_hash: token, type: 'magiclink' });
  if (error !== null) throw new Error(error.message);
  return true;
}


export async function signOut(): Promise<void> {
  await supabase().auth.signOut();
}

export async function currentSession(): Promise<Session | null> {
  const { data } = await supabase().auth.getSession();
  return data.session;
}

/** The access token for API calls, refreshed by the client as needed. */
export async function accessToken(): Promise<string | null> {
  const session = await currentSession();
  return session?.access_token ?? null;
}

/**
 * A new access token, asked for now rather than when the client gets round to it.
 *
 * The background refresh is a timer, and timers do not run in a sleeping
 * laptop or a throttled background tab: a portal left open overnight wakes up
 * holding a token that expired hours ago. Null means the refresh token is no
 * good either, and only signing in again will help.
 */
export async function refreshedAccessToken(): Promise<string | null> {
  const { data, error } = await supabase().auth.refreshSession();
  return error === null ? (data.session?.access_token ?? null) : null;
}

/**
 * Drop a session the server no longer accepts, so the sign-in page appears.
 *
 * Local scope: the server has already said the session is dead, so there is
 * nothing to revoke there, and asking it to would fail with the same token.
 * Signing out fires the auth listener in `useAuth`, which is what swaps the
 * whole shell for the sign-in page — rather than leaving every panel in it
 * showing an error the person cannot act on.
 */
export async function endExpiredSession(): Promise<void> {
  await supabase().auth.signOut({ scope: 'local' });
}
